import fetch from 'isomorphic-fetch';
import _debounce from 'lodash/debounce';
import _find from 'lodash/find';
import _findIndex from 'lodash/findIndex';
import _indexOf from 'lodash/indexOf';
import _isEmpty from 'lodash/isEmpty';
import _last from 'lodash/last';
import _omit from 'lodash/omit';
import _some from 'lodash/some';
import _sortBy from 'lodash/sortBy';
import _without from 'lodash/without';
import moment from 'moment';

import { showConfirmationModal } from 'actions/confirmationModal';
import { showErrorBar } from 'actions/errorBar';
import { toggleSelectWorkspaceModal } from 'actions/workspace';
import { ERROR } from 'constants/error-severities';
import {
  BULK_MOVED,
  CLICKED_JTBD_FOLDER,
  FOLDER_ARCHIVED,
  FOLDER_FAVORITED,
  FOLDER_MOVED,
  SHARE_FOLDER_FOLDER_CLICKED,
  VIDEO_ARCHIVED,
  VIDEO_FAVORITED,
  VIDEO_MOVED,
  VIDEO_RESTORED,
} from 'constants/events';
import {
  ROLLOUT_GRAPHQL_GET_VIDEOS_DASHBOARD,
  ROLLOUT_WELCOME_LOOM,
} from 'constants/featureFlag';
import {
  ALL_SPECIAL_FOLDER_IDS,
  FOLDER_ID_FAVORITES,
  FOLDER_NAME_HOW_TO_USE_LOOM,
  FOLDER_NAME_JTBD,
  FOLDER_NAME_OTHER,
  MY_VIDEOS_FOLDER_NAME,
  PAGETYPE_NAME_PERSONAL,
  PAGETYPE_NAME_SHARED,
  PAGETYPE_NAME_STARRED,
  PAGETYPE_NAME_TEAM,
  SHARED_VIDEOS_FOLDER_NAME,
  SPECIAL_FOLDER_ARCHIVED,
  SPECIAL_FOLDER_FAVORITES,
  SPECIAL_FOLDER_ID_ARCHIVED,
  SPECIAL_FOLDER_ID_MY_VIDEOS,
  SPECIAL_FOLDER_ID_SHARED_VIDEOS,
  SPECIAL_FOLDER_ID_TEAM_ARCHIVED,
  SPECIAL_FOLDER_ID_TEAM_VIDEOS,
  SPECIAL_FOLDER_MY_VIDEOS,
  SPECIAL_FOLDER_SHARED_WITH_ME,
  TEAM_VIDEOS_FOLDER_NAME,
} from 'constants/folders';
import { HEADER_CONTENT_TYPE } from 'constants/http';
import { SHARE_PAGE } from 'constants/menuPages';
import { JSON as JSON_MIME } from 'constants/mimes';
import { V4 } from 'constants/recordingVersions';
import {
  ARCHIVED,
  FAVORITES_PAGE,
  MY_VIDEOS_PAGE,
  ROOT_PAGE,
  SHARED_VIDEOS_PAGE,
  TEAM_VIDEOS_PAGE,
} from 'constants/routes';
import {
  FOLDER,
  FOLDERS_QUERY_LIMIT,
  VIDEO,
  VIDEOS_QUERY_LIMIT,
} from 'constants/videosDashboard';
import { appendAndGotoUrlCreator } from 'creators/routing';
import * as analytics from 'utilities/analytics';
import { getFlagValueForUser } from 'utilities/featureFlag';
import { getGraphQLClient } from 'utilities/graphql';
import * as logger from 'utilities/loggerx';
import * as sentry from 'utilities/sentry';
import { truncateText } from 'utilities/text';
import { getDuration } from 'utilities/video';

import AddFavorite from './AddFavorite.graphql';
import BulkDeleteFolders from './BulkDeleteFolders.graphql';
import BulkDeleteVideos from './BulkDeleteVideos.graphql';
import DeleteFavorite from './DeleteFavorite.graphql';
import DestroyVideo from './DestroyVideo.graphql';
import GetLibraryVideoCountsForWorkspace from './GetLibraryVideoCountsForWorkspace.graphql';
import GetRecentTeamVideos from './GetRecentTeamVideos.graphql';
import GetSharedFoldersOwned from './GetSharedFoldersOwned.graphql';
import GetSharedVideosOwned from './GetSharedVideosOwned.graphql';
import getTopLevelSharedEntities from './GetTopLevelSharedEntities.graphql';
import GetUserFavorites from './GetUserFavorites.graphql';
import getVideosInFolderQuery from './GetVideosInFolder.graphql';
import ToggleSharedFolder from './ToggleSharedFolder.graphql';
import GetWelcomeLoomsForUser from '../creators/GetWelcomeLoomsForUser.gql';
import { updatePreviewShareStatus } from '../actions/previewPanel';
import {
  clearCheckedItems,
  closeDropdowns,
  dashboardInit,
  fetchFavorites,
  fetchFolders,
  fetchHierarchy,
  fetchTopLevelFolders,
  fetchVideos,
  folderDescriptionChanged,
  renameCard,
  resetVideos,
  restoreFromPast,
  shareFolderFailed,
  shareFolderLoading,
  shareFolderSuccess,
  showDropdown,
  unshareFolderFailed,
  unshareFolderLoading,
  unshareFolderSuccess,
  updateAllRecentVideosFetched,
  updateAllVideosFetched,
  updateBreadcrumbs,
  updateCheckedItems,
  updateCurrentFolderInfo,
  updateFavorites,
  updateFolderChangesPending,
  updateFolderName,
  updateFolders,
  updateFoldersError,
  updateFoldersHierarchy,
  updateHasFetchedRecentTeamVideos,
  updateIsArchived,
  updateIsFavorite,
  updateIsFavoritesPage,
  updateIsJTBD,
  updateIsSharedWithMePage,
  updatePast,
  updatePastFolderName,
  updateRecentTeamVideos,
  updateSelectedWorkspace,
  updateTopLevelFolders,
  updateTotalVideoCount,
  updateUserFavoriteFolder,
  updateUserFavoriteVideo,
  updateVideoName,
  updateVideos,
  updateVideosError,
  updateVideosGrid,
  updateWelcomeLooms,
  updateWelcomeLoomsFetched,
  updateWelcomePersonaText,
  updateWorkspaceLibraryVideoCounts,
} from '../actions/videosDashboard';
import {
  cardHasBeenCompleted,
  markEligibleElementAsCompleteForSelectedWorkspace,
} from '../components/videos-dashboard-ta/dashboard-onboarding/utilities';
import { TEAM_ACCOUNT_TYPES } from '../../../shared/constants/accountTypes';
import { LOOM_PRO as LOOM_PRO_CAPABILITY } from '../../../shared/constants/capabilities';
import { WORKSPACE_ACCESS_DENIED } from '../../../shared/constants/errors';
import { DASHBOARD } from '../../../shared/constants/uiLocations';
import {
  ADD_VIDEO_TO_TEAM_CARD_KEY,
  ORGANIZE_FOLDERS_CARD_KEY,
  SHARE_VIDEO_CARD_KEY,
} from '../../../shared/constants/workspaceOnboarding';
import {
  SHARED_WORKSPACE,
  TEAM_WORKSPACE,
} from '../../../shared/constants/workspaces';
import {
  BANNED_CHARS_ERROR_MESSAGE,
  BANNED_CHARS_REGEX,
  URL_FOLDER_ID_REGEX,
} from '../../../shared/validate';

const keysToOmitFromPast = [
  'past',
  'error',
  'is_fetching_folders',
  'is_fetching_videos',
  'card_dropdown_id',
  'rename_id',
  'show_move_modal',
];

function _showAndLogError(error, message) {
  return dispatch => {
    logger.error(error, { message }, { team: 'company' });
    dispatch(showErrorBar(ERROR, message));
  };
}

function _throwOnHttpError(response, dispatch) {
  if (response.status >= 400) {
    return response.json().then(resp => {
      const { error } = resp;

      switch (error) {
        case WORKSPACE_ACCESS_DENIED:
          dispatch(toggleSelectWorkspaceModal(true));
          break;
        default:
          throw new Error(
            `Oops! Something went wrong - HTTP response: ${response.status}`
          );
      }

      return Promise.reject();
    });
  }

  return response;
}

export function dashboardInitCreator(
  rootFolderId,
  rootIsPublic,
  rootIsArchived,
  splat,
  rootIsFavorites,
  rootIsSharedVideos
) {
  return dispatch => {
    const subPath = splat ? splat.trim().split('/') : [];

    dispatch(updateIsArchived(Boolean(rootIsArchived)));
    dispatch(updateIsFavoritesPage(Boolean(rootIsFavorites)));
    dispatch(updateIsSharedWithMePage(Boolean(rootIsSharedVideos)));
    dispatch(dashboardInit(rootIsPublic, rootFolderId));

    if (rootIsPublic) {
      dispatch(loadPublicFolderCreator(rootFolderId, subPath));
    }
  };
}

export function addToPastCreator() {
  return (dispatch, getState) => {
    const videosDashboardState = getState().videos_dashboard;

    const { past } = videosDashboardState;
    const newPast = _omit(videosDashboardState, keysToOmitFromPast);

    dispatch(updatePast(past.concat([newPast])));
  };
}

export function archiveRestoreFolderCreator(id, archived, newParentFolderId) {
  return (dispatch, getState) => {
    const {
      folder_special_id: currentFolderSpecialId,
      top_level_folders: topLevelFolders,
      favorites,
      folders,
      workspace,
    } = getState().videos_dashboard;

    const favoriteToRemove = _find(favorites, { id });
    const folderToUpdate = _find(folders, { id });
    const newFoldersArr = _without(folders, folderToUpdate);

    dispatch(updateFolders(newFoldersArr));

    if (currentFolderSpecialId === SPECIAL_FOLDER_ID_MY_VIDEOS) {
      dispatch(updateTopLevelFolders(newFoldersArr));
    }

    if (archived && favoriteToRemove) {
      const newFavoritesArray = _without(favorites, favoriteToRemove);

      dispatch(updateFavorites(newFavoritesArray));
    }

    let body;

    if (newParentFolderId != null) {
      body = JSON.stringify({
        new_parent_folder_id: newParentFolderId,
        archived,
      });
    } else {
      body = JSON.stringify({ archived });
    }

    return fetch(`/v1/folders/${id}/archive`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'PATCH',
      body,
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .then(() => {
        if (archived) {
          dispatch(fetchFavoritesCreator());
        }

        if (newParentFolderId === SPECIAL_FOLDER_ID_MY_VIDEOS) {
          dispatch(
            updateTopLevelFolders(topLevelFolders.concat([folderToUpdate]))
          );
        }

        const confirmationVerb = archived ? 'archived' : 'restored';
        const confirmationMessage = `${truncateText(
          folderToUpdate.name
        )} successfully ${confirmationVerb}`;

        dispatch(showConfirmationModal(confirmationMessage));

        analytics.track(FOLDER_ARCHIVED, {
          archiving: archived,
          id,
          workspace,
        });
      })
      .catch(err => {
        if (err) {
          dispatch(_showAndLogError(err, 'Oops! Error archiving folder'));
          dispatch(updateFavorites(favorites));
          dispatch(updateFolders(folders));
        }
      });
  };
}

export function archiveRestoreVideoCreator(id, archived) {
  return (dispatch, getState) => {
    const {
      folder_id: currentFolderId,
      is_archived: isArchived,
      breadcrumbs,
      recentTeamVideos,
      videos,
      workspace,
    } = getState().videos_dashboard;

    let videoToUpdate = videos.find(video => video.id === id);

    if (!videoToUpdate) {
      videoToUpdate = recentTeamVideos.find(video => video.id === id);
    }

    const { capabilities } = getState().user;
    const newVideosArr = _without(videos, videoToUpdate);
    const hasLoomPro = capabilities.includes(LOOM_PRO_CAPABILITY);

    let newFolderId;

    if (archived) {
      newFolderId =
        workspace === TEAM_WORKSPACE
          ? SPECIAL_FOLDER_ID_TEAM_ARCHIVED
          : SPECIAL_FOLDER_ID_ARCHIVED;
    } else {
      newFolderId =
        workspace === TEAM_WORKSPACE
          ? SPECIAL_FOLDER_ID_TEAM_VIDEOS
          : SPECIAL_FOLDER_ID_MY_VIDEOS;
    }

    dispatch(updateVideosGrid(newVideosArr));

    const body = JSON.stringify({
      folder_id: newFolderId,
      archived,
    });

    return fetch(`/v1/videos/${id}`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'PATCH',
      body,
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .then(() => {
        if (hasLoomPro || !archived) {
          return;
        }

        return dispatch(fetchVideosCreator(null, true));
      })
      .then(() => dispatch(updateTotalVideoCountCreator()))
      .then(() => {
        const trackEvent = archived ? VIDEO_ARCHIVED : VIDEO_RESTORED;

        analytics.track(trackEvent, {
          archiving: archived,
          current_folder_id: currentFolderId,
          depth: breadcrumbs.length - 1,
          is_archived: isArchived,
          new_folder_id: newFolderId,
          video_name: videoToUpdate.name,
          id,
          workspace,
        });

        const confirmationVerb = archived ? 'archived' : 'restored';
        const confirmationMessage = `${truncateText(
          videoToUpdate.name
        )} successfully ${confirmationVerb}`;

        dispatch(showConfirmationModal(confirmationMessage));
        dispatch(fetchLibraryVideoCountsCreator());
      })
      .catch(err => {
        if (err) {
          dispatch(_showAndLogError(err, 'Oops! Error while restoring folder'));
          dispatch(updateVideosGrid(videos));
        }
      });
  };
}

export function bulkMoveFoldersVideosCreator(
  archive,
  newParentFolderId,
  passedCheckedItems,
  destinationWorkspace
) {
  return (dispatch, getState) => {
    const {
      checked_items: checkedItemsFromState,
      folder_id: folderId,
      folder_special_id: currentFolderSpecialId,
      is_archived: isArchived,
      top_level_folders: topLevelFolders,
      total_video_count: totalVideoCount,
      breadcrumbs,
      folders,
      root_folder_id,
      videos,
      workspace,
    } = getState().videos_dashboard;

    const workspaceReducer = getState().workspace;

    const checkedItems = passedCheckedItems || checkedItemsFromState;

    if (checkedItems.length === 0) {
      return;
    }

    const checkedFolderItems = checkedItems.filter(
      item => item.type === FOLDER
    );
    const checkedVideoItems = checkedItems.filter(item => item.type === VIDEO);

    const checkedFolders = checkedFolderItems.map(folderItem =>
      _find(folders, { id: folderItem.id })
    );

    const checkedVideos = checkedVideoItems.map(videoItem =>
      _find(videos, { id: videoItem.id })
    );
    const newFoldersArr = _without(folders, ...checkedFolders);
    const newVideosArr = _without(videos, ...checkedVideos);
    const errVerb = newParentFolderId
      ? 'moving'
      : archive
      ? 'archiving'
      : 'restoring';
    const successVerb = newParentFolderId
      ? 'moved'
      : archive
      ? 'archived'
      : 'restored';

    if (checkedFolders.length <= 0 && checkedVideos.length <= 0) {
      const errString = `attempted bulk ${errVerb} without selection`;

      return sentry.logException(new Error(errString));
    }

    const promises = [];
    const organization_id =
      checkedFolders[0]?.organization_id || checkedVideos[0]?.organization_id;

    if (checkedFolderItems.length > 0) {
      const folderBody = JSON.stringify({
        folder_ids: checkedFolderItems.map(folderItem => folderItem.id),
        new_parent_folder_id: newParentFolderId ? newParentFolderId : null,
        archive,
      });

      dispatch(updateFolders(newFoldersArr));

      if (currentFolderSpecialId === SPECIAL_FOLDER_ID_MY_VIDEOS) {
        dispatch(updateTopLevelFolders(newFoldersArr));
      }

      promises.push(
        fetch('/v1/folders/bulk_move', {
          body: folderBody,
          credentials: 'include',
          headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
          method: 'PATCH',
        })
          .then(response => _throwOnHttpError(response, dispatch))
          .then(() => {
            if (archive) {
              dispatch(fetchFavoritesCreator());
            }

            if (newParentFolderId === SPECIAL_FOLDER_ID_MY_VIDEOS) {
              dispatch(
                updateTopLevelFolders(topLevelFolders.concat(checkedFolders))
              );
            }
          })
          .catch(err => {
            if (err) {
              dispatch(_showAndLogError(err, `error bulk ${errVerb} folders`));
              dispatch(updateFolders(folders));

              if (currentFolderSpecialId === SPECIAL_FOLDER_ID_MY_VIDEOS) {
                dispatch(updateTopLevelFolders(newFoldersArr));
              }
            }
          })
      );
    }

    if (checkedVideoItems.length > 0) {
      const destinationFolderId = newParentFolderId ? newParentFolderId : null;
      const videoBody = JSON.stringify({
        video_ids: checkedVideoItems.map(videoItem => videoItem.id),
        new_parent_folder_id: destinationFolderId,
        archive,
      });

      dispatch(updateVideosGrid(newVideosArr));

      if (archive) {
        dispatch(
          updateTotalVideoCount(
            Math.max(0, totalVideoCount - checkedVideoItems.length)
          )
        );
      }

      promises.push(
        fetch('/v1/videos/bulk_move', {
          body: videoBody,
          credentials: 'include',
          headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
          method: 'PATCH',
        })
          .then(response => _throwOnHttpError(response, dispatch))
          .then(() => {
            applyOnboardingStateChanges(
              dispatch,
              workspaceReducer.workspaces,
              destinationWorkspace
            );

            dispatch(fetchLibraryVideoCountsCreator());
          })
          .catch(err => {
            if (err) {
              dispatch(_showAndLogError(err, `error bulk ${errVerb} videos`));
              dispatch(updateVideosGrid(videos));
            }
          })
      );
    }

    const checkedUploadedVideos = checkedVideoItems
      .map(({ id }) => id)
      .filter(id => {
        const video = videos.find(vid => vid.id === id);

        return video?.video_properties?.recording_version === V4;
      });

    Promise.all(promises).then(() => {
      analytics.track(BULK_MOVED, {
        archiving: archive,
        current_folder_id: folderId,
        current_folder_special_id: currentFolderSpecialId,
        root_folder_id,
        depth: breadcrumbs.length - 1,
        is_archived: isArchived,
        new_parent_folder_id: newParentFolderId,
        num_folders: checkedFolderItems.length,
        num_videos: checkedVideoItems.length,
        num_external_videos: checkedUploadedVideos.length,
        from_library: workspace,
        to_library: destinationWorkspace,
        organization_id,
      });

      // Track video_moved for each checked video
      checkedVideoItems.map(video => {
        analytics.track(VIDEO_MOVED, {
          archiving: archive,
          current_folder_id: folderId,
          depth: breadcrumbs.length - 1,
          is_archived: isArchived,
          new_folder_id: newParentFolderId,
          video_name: video.name,
          recording_version: video.video_properties?.recording_version,
          id: video.id,
          from_library: workspace,
          to_library: destinationWorkspace,
          organization_id: video.organization_id,
          source: 'bulk-move',
        });
      });

      let itemsString;

      if (checkedFolderItems.length > 0 && checkedVideoItems.length > 0) {
        itemsString = 'items';
      } else if (checkedFolderItems.length > 0) {
        itemsString = 'folders';
      } else {
        itemsString = 'videos';
      }

      dispatch(clearCheckedItems());

      const confirmationString = `${checkedItems.length} ${itemsString} successfully ${successVerb}`;

      dispatch(showConfirmationModal(confirmationString));
    });
  };
}

function _newNameMatchesOldName({ folders, id, name }) {
  const folder = folders.find(folder => folder.id === id);

  return folder?.name === name;
}

function _validateFolderName(folders, name) {
  const hasDuplicateName = _some(
    folders,
    folder => folder.name.toLowerCase() === name.toLowerCase()
  );

  if (BANNED_CHARS_REGEX.test(name)) {
    return BANNED_CHARS_ERROR_MESSAGE;
  } else if (hasDuplicateName) {
    return 'Cannot have duplicate folder names';
  }

  return null;
}

export function commitRenameFolderCreator(id, name) {
  return (dispatch, getState) => {
    const {
      folder_special_id: currentFolderSpecialId,
      favorites,
      folders,
      past,
      breadcrumbs,
    } = getState().videos_dashboard;

    if (_newNameMatchesOldName({ folders, id, name })) {
      // no-op, bail out of updating folder name
      return;
    }

    const validationError = _validateFolderName(folders, name);

    if (validationError !== null) {
      dispatch(showErrorBar(ERROR, validationError));

      return;
    }

    dispatch(updateFolderChangesPending(true));

    const folderToReplace = _find(folders, { id });
    const favoriteToReplace = _find(favorites, { id });
    const newFolder = Object.assign({}, folderToReplace, { name });
    const shouldUpdateBreadcrumbs =
      breadcrumbs[breadcrumbs.length - 1].id === id;

    if (shouldUpdateBreadcrumbs) {
      const copy = JSON.parse(JSON.stringify(breadcrumbs));

      const newBreadcrumbs = copy.map(b => {
        if (b.id === id) {
          b.name = name;
        }

        return b;
      });

      const pathname = window.location.pathname.split('/');

      dispatch(updateBreadcrumbs(newBreadcrumbs));
      dispatch(updatePastFolderName(id, name));

      if (pathname.length) {
        pathname[pathname.length - 1] = name;
        window.history.replaceState({}, '', `${pathname.join('/')}`);
      }
    }

    const newFoldersArr = folders.map(folder => {
      if (folder.id === newFolder.id) {
        return newFolder;
      }

      return folder;
    });

    if (favoriteToReplace) {
      const newFavoritesArray = favorites.map(favoriteFolder => {
        if (favoriteFolder.id === newFolder.id) {
          return newFolder;
        }

        return favoriteFolder;
      });

      dispatch(updateFavorites(newFavoritesArray));

      if (past.length > 0) {
        const newPast = past.map(pastItem => {
          return Object.assign({}, pastItem, {
            favorites: newFavoritesArray,
          });
        });

        dispatch(updatePast(newPast));
      }
    }

    dispatch(updateFolderName(id, name));

    if (currentFolderSpecialId === SPECIAL_FOLDER_ID_MY_VIDEOS) {
      dispatch(updateTopLevelFolders(newFoldersArr));
    }

    const body = JSON.stringify({ name });

    return fetch(`/v1/folders/${id}`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'PATCH',
      body,
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .then(() => {
        dispatch(updateFolderChangesPending(false));
      })
      .catch(err => {
        if (err) {
          dispatch(_showAndLogError(err, 'Oops! Error renaming folder'));
          dispatch(updateFolders(folders));

          if (favoriteToReplace) {
            dispatch(updateFavorites(favorites));
          }

          if (currentFolderSpecialId === SPECIAL_FOLDER_ID_MY_VIDEOS) {
            dispatch(updateTopLevelFolders(folders));
          }

          dispatch(updateFolderChangesPending(false));
        }
      });
  };
}

export function commitRenameVideoCreator(id, name) {
  return (dispatch, getState) => {
    const { videos } = getState().videos_dashboard;

    dispatch(updateVideoName(id, name));

    const body = JSON.stringify({ name });

    return fetch(`/v1/videos/${id}`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'PATCH',
      body,
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .catch(err => {
        if (err) {
          dispatch(_showAndLogError(err, 'Oops! Error renaming video'));
          dispatch(updateVideosGrid(videos));
          dispatch(showErrorBar(ERROR, err));
        }
      });
  };
}

export function createFolderCreator(parentFolderId = null, shared = false) {
  return (dispatch, getState) => {
    const { videos_dashboard, workspace: workspaceRed } = getState();

    const {
      folder_changes_pending: folderChangesPending,
      folder_special_id: currentFolderSpecialId,
      is_fetching_folders: isFetchingFolders,
      folders,
      workspace,
    } = videos_dashboard;

    const body = JSON.stringify({
      parent_folder_id: parentFolderId,
      shared,
      workspace,
    });

    if (folderChangesPending || isFetchingFolders) {
      return;
    }

    dispatch(updateFolderChangesPending(true));

    return fetch('/v1/folders', {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'POST',
      body,
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .then(resp => resp.json())
      .then(folderJSON => {
        const newFoldersArr = folders.slice(0);

        newFoldersArr.unshift(folderJSON);

        dispatch(updateFolders(newFoldersArr));
        dispatch(renameCard(folderJSON.id));
        dispatch(updateFolderChangesPending(false));

        if (currentFolderSpecialId === SPECIAL_FOLDER_ID_MY_VIDEOS) {
          dispatch(updateTopLevelFolders(newFoldersArr));
        }

        if (currentFolderSpecialId === SPECIAL_FOLDER_ID_TEAM_VIDEOS) {
          markEligibleElementAsCompleteForSelectedWorkspace(
            dispatch,
            workspaceRed.workspaces,
            ORGANIZE_FOLDERS_CARD_KEY
          );
        }
      })
      .catch(err => {
        if (err) {
          dispatch(_showAndLogError(err, 'Oops! Error creating folder'));
          dispatch(updateFolders(folders));
          dispatch(updateFolderChangesPending(false));
        }
      });
  };
}

export function favoriteCurrentFolderCreator(id, favorite) {
  return (dispatch, getState) => {
    const {
      folder_changes_pending: folderChangesPending,
      folder_id: currentFolderId,
      folder_name: folderName,
      folder_special_id: current_folder_special_id,
      is_favorite: isFavorite,
      breadcrumbs,
      favorites,
      past,
      workspace,
    } = getState().videos_dashboard;

    if (folderChangesPending || _indexOf(ALL_SPECIAL_FOLDER_IDS, id) > -1) {
      return;
    }

    dispatch(updateIsFavorite(favorite));
    dispatch(updateFolderChangesPending(true));

    let newFavoritesArray;

    if (
      favorite &&
      !_some(favorites, favoriteFolder => favoriteFolder.id === id)
    ) {
      newFavoritesArray = favorites.concat([
        {
          name: folderName,
          id,
        },
      ]);
    } else {
      const favoriteToRemove = _find(favorites, { id });

      newFavoritesArray = _without(favorites, favoriteToRemove);
    }

    dispatch(updateFavorites(newFavoritesArray));

    if (past.length > 0) {
      const newPast = past.map(pastItem => {
        return Object.assign({}, pastItem, {
          favorites: newFavoritesArray,
        });
      });

      const mostRecentPastItem = _last(newPast);

      const folderToReplace = _find(mostRecentPastItem.folders, { id });
      const newFolder = Object.assign({}, folderToReplace, { favorite });

      const newFoldersArr = mostRecentPastItem.folders.map(folder => {
        if (folder.id === newFolder.id) {
          return newFolder;
        }

        return folder;
      });

      mostRecentPastItem.folders = newFoldersArr;

      dispatch(updatePast(newPast));
    }

    const body = JSON.stringify({ favorite });

    return fetch(`/v1/folders/${id}`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'PATCH',
      body,
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .then(() => {
        analytics.track(FOLDER_FAVORITED, {
          current: true,
          current_parent_folder_id: currentFolderId,
          current_folder_special_id,
          depth: breadcrumbs.length - 1,
          favorite,
          workspace,
          id,
        });
      })
      .then(() => dispatch(updateFolderChangesPending(false)))
      .catch(err => {
        if (err) {
          dispatch(
            _showAndLogError(err, 'Oops! Error marking folder as favorite')
          );
          dispatch(updateFavorites(favorites));
          dispatch(updateIsFavorite(isFavorite));
          dispatch(updateFolderChangesPending(false));
        }
      });
  };
}

export function favoriteFolderCreator(id, favorite) {
  return (dispatch, getState) => {
    const {
      folder_changes_pending: folderChangesPending,
      folder_id: currentFolderId,
      folder_special_id: current_folder_special_id,
      top_level_folders: topLevelFolders,
      breadcrumbs,
      favorites,
      folders,
      past,
      workspace,
    } = getState().videos_dashboard;

    if (folderChangesPending) {
      return;
    }

    dispatch(updateFolderChangesPending(true));

    const folderToReplace = _find(folders, { id });

    const topLevelFolderToReplace = _find(topLevelFolders, { id });

    let newFolder;

    if (folderToReplace) {
      newFolder = Object.assign({}, folderToReplace, { favorite });

      const newFoldersArr = folders.map(folder => {
        if (folder.id === newFolder.id) {
          return newFolder;
        }

        return folder;
      });

      dispatch(updateFolders(newFoldersArr));
    } else {
      newFolder = Object.assign({}, topLevelFolderToReplace, { favorite });
    }

    let newFavoritesArray;

    if (
      favorite &&
      !_some(favorites, favoriteFolder => favoriteFolder.id === id)
    ) {
      newFavoritesArray = favorites.concat([newFolder]);
    } else {
      const favoriteToRemove = _find(favorites, { id });

      newFavoritesArray = _without(favorites, favoriteToRemove);
    }

    dispatch(updateFavorites(newFavoritesArray));

    if (past.length > 0) {
      const newPast = past.map(pastItem => {
        return Object.assign({}, pastItem, {
          favorites: newFavoritesArray,
        });
      });

      dispatch(updatePast(newPast));
    }

    const body = JSON.stringify({ favorite });

    return fetch(`/v1/folders/${id}`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'PATCH',
      body,
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .then(() => {
        analytics.track(FOLDER_FAVORITED, {
          current: false,
          current_parent_folder_id: currentFolderId,
          current_folder_special_id,
          depth: breadcrumbs.length - 1,
          favorite,
          workspace,
          id,
        });
      })
      .then(() => dispatch(updateFolderChangesPending(false)))
      .catch(err => {
        if (err) {
          dispatch(
            _showAndLogError(err, 'Oops! Error marking folder as favorite')
          );
          dispatch(updateFolders(folders));
          dispatch(updateFavorites(favorites));
          dispatch(updateFolderChangesPending(false));
        }
      });
  };
}

function _getEndpointFolderId(isArchived, folderId) {
  if (isArchived && !folderId) {
    return SPECIAL_FOLDER_ID_ARCHIVED;
  } else if (!isArchived && !folderId) {
    return SPECIAL_FOLDER_ID_MY_VIDEOS;
  }

  return folderId;
}

export function fetchFavoritesCreator() {
  return (dispatch, getState) => {
    const { is_fetching_favorites: isFetching } = getState().videos_dashboard;
    const { is_logged_in, is_logged_in_checked } = getState().user;
    const isLoggedIn = is_logged_in_checked && is_logged_in;

    if (isFetching || !isLoggedIn) {
      return;
    }

    dispatch(fetchFavorites());

    return fetch('/v1/folders/favorites', {
      credentials: 'include',
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .then(resp => resp.json())
      .then(foldersJSON => {
        dispatch(updateFavorites(foldersJSON));
      })
      .catch(err => {
        if (err) {
          dispatch(
            _showAndLogError(err, 'Oops! Error fetching favorite folders')
          );
        }
      });
  };
}

export function fetchAllParentsForBreadcrumbCreator(folderId) {
  return (dispatch, getState) => {
    const {
      folder_name: folderName,
      folder_shared: folderShared,
      root_folder_id: rootFolderId,
      is_favorites_page: isFavoritesPage,
      is_shared_with_me_page: isSharedWithMePage,
    } = getState().videos_dashboard;
    const { current: currentUser } = getState().user;

    return fetch(`/v1/folders/${folderId}/parents?root=${rootFolderId}`, {
      credentials: 'include',
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .then(resp => resp.json())
      .then(foldersJSON => {
        const breadcrumbFolders = foldersJSON.reverse().concat({
          id: folderId,
          name: folderName,
          shared: folderShared,
        });

        const breadcrumbs = breadcrumbFolders.map(folder => {
          /**
           * The dashboard in the team accounts UI does not use the folder name for the initial
           * breadcrumb value so we have to manually perform some translation to convert the folder
           * name to the page type.
           */
          let breadcrumbPageType;

          switch (folder.name) {
            case MY_VIDEOS_FOLDER_NAME:
              breadcrumbPageType = PAGETYPE_NAME_PERSONAL;
              break;
            case TEAM_VIDEOS_FOLDER_NAME:
              breadcrumbPageType = PAGETYPE_NAME_TEAM;
              break;
            case SHARED_VIDEOS_FOLDER_NAME:
              breadcrumbPageType = PAGETYPE_NAME_SHARED;
              break;
            default:
              breadcrumbPageType = folder.name;
          }

          return {
            id: folder.id,
            name: TEAM_ACCOUNT_TYPES.includes(currentUser.account_type)
              ? breadcrumbPageType
              : folder.name,
            shared: folder.shared,
          };
        });

        // Since favorites is not a real parent folder
        // it will not be included
        // in the API response so we have to manually add it to the front of
        // the breadcrumbs array.
        if (isFavoritesPage) {
          breadcrumbs.unshift({
            id: FOLDER_ID_FAVORITES,
            name: PAGETYPE_NAME_STARRED,
            shared: false,
          });
        }

        // If the user on in the shared library but the first breadcrumb is not
        // the shared videos page type, then they are viewing content that is from
        // another user's shared-videos folder (since each user has their own), so
        // we need to manually add it to the front of the breadcrumbs
        if (
          isSharedWithMePage &&
          breadcrumbs[0].name !== PAGETYPE_NAME_SHARED
        ) {
          breadcrumbs.unshift({
            id: SPECIAL_FOLDER_ID_SHARED_VIDEOS,
            name: PAGETYPE_NAME_SHARED,
            shared: false,
          });
        }

        dispatch(updateBreadcrumbs(breadcrumbs));
      })
      .catch(err => {
        if (err) {
          dispatch(
            _showAndLogError(err, 'Oops! Error fetching folder breadcrumbs')
          );
        }
      });
  };
}

export function fetchAllParentsForFavoriteCreator(folderId, isArchived) {
  return (dispatch, getState) => {
    const { favorites } = getState().videos_dashboard;
    const currentFavorite = _find(favorites, { id: folderId });

    const rootFolderId = isArchived
      ? SPECIAL_FOLDER_ARCHIVED.specialId
      : SPECIAL_FOLDER_MY_VIDEOS.specialId;

    return fetch(`/v1/folders/${folderId}/parents?root=${rootFolderId}`, {
      credentials: 'include',
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .then(resp => resp.json())
      .then(foldersJSON => {
        const parentsWithoutTopLevel = foldersJSON.reverse().slice(1);

        const encodedNames = parentsWithoutTopLevel.map(parentFolder => {
          return encodeURIComponent(parentFolder.name);
        });

        const encodedNamesString =
          encodedNames.length > 0 ? `${encodedNames.join('/')}/` : '';

        window.location.href = `${MY_VIDEOS_PAGE}/${encodedNamesString}${currentFavorite.name}`;
      })
      .catch(err => {
        if (err) {
          dispatch(
            _showAndLogError(err, 'Oops! Error in fetch favorite folders')
          );
        }
      });
  };
}

export function fetchFoldersCreator(
  folderId,
  folderName,
  folderDescription,
  favorite = false,
  collaborators
) {
  return (dispatch, getState) => {
    const {
      folder_id: currentFolderId,
      folder_name: currentFolderName,
      folder_description: currentFolderDescription,
      folder_shared: currentFolderShared,
      is_archived: isArchived,
      is_favorite: isFavorite,
      parent_folder_id: currentParentFolderId,
      folder_owner_id: currentParentOwnerId,
      folder_collaborators: currentFolderCollaborators,
      folder_owner: currentFolderOwner,
    } = getState().videos_dashboard;

    const endpointFolderId = _getEndpointFolderId(isArchived, folderId);
    const endpoint =
      `/v1/folders/${endpointFolderId}?content_type=folder` +
      `&limit=${FOLDERS_QUERY_LIMIT}`;

    dispatch(fetchFolders());

    if (endpointFolderId === SPECIAL_FOLDER_ID_MY_VIDEOS) {
      dispatch(updateCurrentFolderInfo(SPECIAL_FOLDER_MY_VIDEOS));
    } else if (endpointFolderId === SPECIAL_FOLDER_ID_ARCHIVED) {
      dispatch(updateCurrentFolderInfo(SPECIAL_FOLDER_ARCHIVED));
    } else {
      dispatch(
        updateCurrentFolderInfo({
          id: folderId,
          name: folderName,
          description: folderDescription,
          parentId: currentFolderId,
          favorite,
          collaborators,
        })
      );
    }

    return fetch(endpoint, {
      credentials: 'include',
    })
      .then(resp => resp.json())
      .then(foldersJSON => {
        const { folders, folder, workspace } = foldersJSON;

        dispatch(
          updateCurrentFolderInfo({
            id: folder.id,
            name: folder.name,
            description: folder.description,
            parentId: folder.parent_folder_id,
            ownerId: folder.owner_id,
            specialId: folder.special_id,
            favorite: folder.favorite,
            shared: folder.shared,
            collaborators: folder.collaborators,
            owner: folder.owner,
          })
        );
        dispatch(updateSelectedWorkspace(workspace));
        dispatch(updateFolders(folders));

        if (endpointFolderId === SPECIAL_FOLDER_ID_MY_VIDEOS) {
          dispatch(updateTopLevelFolders(folders));
        }
      })
      .catch(err => {
        dispatch(_showAndLogError(err, 'Oops! Error while fetching folders'));

        // Error: Restore old values from before optimistic load
        dispatch(
          updateCurrentFolderInfo({
            id: currentFolderId,
            name: currentFolderName,
            description: currentFolderDescription,
            parentId: currentParentFolderId,
            ownerId: currentParentOwnerId,
            favorite: isFavorite,
            shared: currentFolderShared,
            collaborators: currentFolderCollaborators,
            owner: currentFolderOwner,
          })
        );
        dispatch(updateFoldersError(err));
      });
  };
}

export function fetchFolderHierarchyCreator(selectedWorkspace = null) {
  return (dispatch, getState) => {
    const { is_archived: isArchived } = getState().videos_dashboard;
    let { workspace } = getState().videos_dashboard;
    const { video_is_team_shared: videoIsTeamShared } = getState().share_video;
    let specialFolderId;

    /**
     * If we are on the video dashboard, the _workspace_ variable will indicate
     * which specialId to send with the api call. If we are on the share
     * page we need to look at _videoIsTeamShared_ instead.
     */

    workspace = selectedWorkspace ? selectedWorkspace : workspace;

    if (isArchived) {
      specialFolderId = SPECIAL_FOLDER_ID_ARCHIVED;
    } else if (videoIsTeamShared) {
      specialFolderId = SPECIAL_FOLDER_ID_TEAM_VIDEOS;
    } else {
      switch (workspace) {
        case TEAM_WORKSPACE:
          specialFolderId = SPECIAL_FOLDER_ID_TEAM_VIDEOS;
          break;
        case SHARED_WORKSPACE:
          specialFolderId = SPECIAL_FOLDER_ID_SHARED_VIDEOS;
          break;
        default:
          specialFolderId = SPECIAL_FOLDER_ID_MY_VIDEOS;
      }
    }

    const endpoint = `/v1/folders/hierarchy?special_id=${specialFolderId}`;

    dispatch(fetchHierarchy());

    return fetch(endpoint, {
      credentials: 'include',
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .then(resp => resp.json())
      .then(hierarchyJSON => dispatch(updateFoldersHierarchy(hierarchyJSON)));
  };
}

export function fetchVideosCreator(folderId, forceRefresh = false) {
  return async (dispatch, getState) => {
    const {
      all_videos_fetched: allVideosFetched,
      is_archived: isArchived,
      is_fetching_videos: isFetching,
      videos,
    } = getState().videos_dashboard;

    const { capabilities } = getState().user;

    if (isFetching || (allVideosFetched && !forceRefresh)) {
      return;
    }

    dispatch(fetchVideos());

    const endpointFolderId = _getEndpointFolderId(isArchived, folderId);

    const isMyVideos = endpointFolderId === SPECIAL_FOLDER_ID_MY_VIDEOS;
    const hasLoomPro = capabilities.includes(LOOM_PRO_CAPABILITY);

    const useGraphQL = await getFlagValueForUser(
      ROLLOUT_GRAPHQL_GET_VIDEOS_DASHBOARD
    );

    if (useGraphQL) {
      // the my-videos root folder for a basic user is all videos instead of
      // an organized folder. Hence we fetch all videos for that case
      const gqlClient = getGraphQLClient();

      return gqlClient
        .query({
          query: getVideosInFolderQuery,
          variables: {
            folderId: endpointFolderId,
            limit: VIDEOS_QUERY_LIMIT,
            archived: isArchived,
            allVideos: isMyVideos && !hasLoomPro,
            includePending: true,
            offsetDate:
              videos.length !== 0 && !forceRefresh
                ? Number(new Date(videos[videos.length - 1].createdAt))
                : null,
          },
        })
        .then(response => {
          return response.data.folderVideos;
        })
        .then(videosJSON => {
          const { videos } = getState().videos_dashboard;
          const { videos: newVideos, allVideosFetched } = videosJSON;

          if (newVideos.length === 0 || allVideosFetched) {
            dispatch(updateAllVideosFetched(true));
          }

          dispatch(
            updateVideos(forceRefresh ? newVideos : videos.concat(newVideos))
          );
        })
        .catch(err => {
          dispatch(_showAndLogError(err, 'Oops! Error while fetching videos'));
          dispatch(updateVideosError(err));
        });
    }
    // the my-videos root folder for a basic user is all videos instead of
    // an organized folder. Hence we fetch all videos for that case

    let endpoint =
      `/v1/folders/${endpointFolderId}?content_type=video` +
      `&limit=${VIDEOS_QUERY_LIMIT}&archived=${isArchived}` +
      `&all_videos=${isMyVideos && !hasLoomPro ? '1' : ''}` +
      `&include_pending=true`;

    if (videos.length !== 0 && !forceRefresh) {
      const lastVideoCreatedAt = new Date(videos[videos.length - 1].createdAt);

      endpoint = `${endpoint}&offsetDate=${Number(lastVideoCreatedAt)}`;
    }

    return fetch(endpoint, {
      credentials: 'include',
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .then(resp => resp.json())
      .then(videosJSON => {
        const { videos } = getState().videos_dashboard;
        const { videos: newVideos, allVideosFetched } = videosJSON;

        if (newVideos.length === 0 || allVideosFetched) {
          dispatch(updateAllVideosFetched(true));
        }

        dispatch(
          updateVideos(forceRefresh ? newVideos : videos.concat(newVideos))
        );
      })
      .catch(err => {
        if (err) {
          dispatch(_showAndLogError(err, 'Oops! Error while fetching videos'));
          dispatch(updateVideosError(err));
        }
      });
  };
}

export function fetchLibraryVideoCountsCreator() {
  return async dispatch => {
    try {
      const gqlClient = getGraphQLClient();

      const { data } = await gqlClient.query({
        query: GetLibraryVideoCountsForWorkspace,
        fetchPolicy: 'no-cache',
      });

      const { videoCounts } = data;

      if (videoCounts.__typename !== 'WorkspaceLibraryVideoCounts') {
        throw Error(videoCounts.message);
      }

      dispatch(updateWorkspaceLibraryVideoCounts(videoCounts));
    } catch (err) {
      sentry.logException(err, 'Error fetching library video counts');
    }
  };
}

export function fetchRecentTeamVideosCreator(forceRefresh = false) {
  return async (dispatch, getState) => {
    try {
      const { recentTeamVideos, allRecentVideosFetched, isFetching } =
        getState().videos_dashboard;

      if (isFetching || (allRecentVideosFetched && !forceRefresh)) {
        return;
      }

      dispatch(updateHasFetchedRecentTeamVideos(false));

      let limit;
      let offset;

      if (forceRefresh) {
        limit = recentTeamVideos.length;
        offset = 0;
      } else {
        limit = VIDEOS_QUERY_LIMIT;
        offset = recentTeamVideos.length;
      }

      const gqlClient = getGraphQLClient();
      const { data } = await gqlClient.query({
        query: GetRecentTeamVideos,
        variables: {
          limit,
          offset,
        },
        fetchPolicy: 'no-cache',
      });

      if (
        data.recentTeamVideos.videos.length === 0 ||
        data.recentTeamVideos.allRecentTeamVideosFetched
      ) {
        dispatch(updateAllRecentVideosFetched(true));
      }

      dispatch(
        updateRecentTeamVideos(
          forceRefresh
            ? data.recentTeamVideos.videos
            : recentTeamVideos.concat(data.recentTeamVideos.videos)
        )
      );
    } catch (err) {
      dispatch(_showAndLogError(err, 'Oops! Error while fetching videos'));
      dispatch(updateHasFetchedRecentTeamVideos(true));
      dispatch(updateVideosError(err));
    }
  };
}

export function fetchTopLevelFoldersCreator() {
  return (dispatch, getState) => {
    const {
      is_fetching_top_level_folders: isFetching,
      top_level_folders: topLevelFolders,
    } = getState().videos_dashboard;

    if (isFetching) {
      return;
    }

    dispatch(fetchTopLevelFolders());

    const endpoint =
      `/v1/folders/${SPECIAL_FOLDER_ID_MY_VIDEOS}?content_type=folder` +
      `&limit=${FOLDERS_QUERY_LIMIT}`;

    return fetch(endpoint, {
      credentials: 'include',
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .then(resp => resp.json())
      .then(foldersJSON => {
        const { folders } = foldersJSON;

        dispatch(updateTopLevelFolders(folders));
      })
      .catch(err => {
        if (err) {
          dispatch(
            _showAndLogError(
              err,
              'Oops! Error while fetching top level folders'
            )
          );
          dispatch(updateTopLevelFolders(topLevelFolders));
        }
      });
  };
}

export function fetchWelcomeLooms() {
  return async (dispatch, getState) => {
    try {
      let welcomeLooms = [];
      let welcomePersonaText = '';
      const { folder_special_id: currentFolderSpecialId } =
        getState().videos_dashboard;

      const showWelcomeLoom = await getFlagValueForUser(ROLLOUT_WELCOME_LOOM);

      if (!showWelcomeLoom) {
        dispatch(updateWelcomeLoomsFetched(true));

        return;
      }

      const welcomeLoomPage =
        currentFolderSpecialId === SPECIAL_FOLDER_ID_ARCHIVED ||
        currentFolderSpecialId === SPECIAL_FOLDER_ID_MY_VIDEOS;

      if (!welcomeLoomPage) {
        dispatch(updateWelcomeLoomsFetched(true));

        return;
      }

      const client = getGraphQLClient();
      const { data } = await client.query({
        query: GetWelcomeLoomsForUser,
      });

      if (data.getWelcomeLoomsForUser) {
        welcomePersonaText = data.getWelcomeLoomsForUser.description;
        // we need to deep clone the array to be able to add properties
        welcomeLooms = JSON.parse(
          JSON.stringify(data.getWelcomeLoomsForUser.videos)
        );
        welcomeLooms.forEach(loom => {
          loom.isAnimated = loom.use_gif;
          loom.isWelcomeLoom = true;
          loom.duration = getDuration(loom);

          loom.thumbnails.video =
            loom.thumbnails?.animatedPreview ||
            loom.signedThumbnails?.animatedPreview;
        });
        dispatch(updateWelcomeLooms(welcomeLooms));
        dispatch(updateWelcomeLoomsFetched(true));
        dispatch(updateWelcomePersonaText(welcomePersonaText));
      }
    } catch (err) {
      sentry.logException(err, 'Error fetching Welcome Loom');
    }
  };
}

export function fetchFoldersVideosByNameCreator(
  pathname,
  parentFolderId = null
) {
  return (dispatch, getState) => {
    const {
      is_archived: isArchived,
      is_fetching_folders: isFetchingFolders,
      is_fetching_videos: isFetchingVideos,
    } = getState().videos_dashboard;

    if (isFetchingFolders || isFetchingVideos) {
      return;
    }

    dispatch(fetchFolders());
    dispatch(fetchVideos());

    const regexWithSlash = new RegExp(
      `^(${MY_VIDEOS_PAGE}|${ARCHIVED}|${TEAM_VIDEOS_PAGE}|${FAVORITES_PAGE}|${SHARED_VIDEOS_PAGE})\/`,
      'i'
    );
    const pathnamesArr = pathname.replace(regexWithSlash, '').split('/');
    const decodedUrlsArr = pathnamesArr
      .map(encodedName => decodeURIComponent(encodedName))
      // must filter out trailing slash
      .filter(decodedName => decodedName.length !== 0);

    const body = JSON.stringify({
      archived: isArchived,
      folder_names: decodedUrlsArr,
      parent_folder_id: parentFolderId,
    });

    return fetch('/v1/folders/by_name', {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'POST',
      body,
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .then(resp => resp.json())
      .then(foldersAndVideosJSON => {
        const {
          current_folder: currentFolder,
          folders,
          videos,
          workspace,
        } = foldersAndVideosJSON;

        const {
          id: currentFolderId,
          name: currentFolderName,
          description: currentFolderDescription,
          parent_folder_id: currentFolderParentId,
          favorite,
          shared,
          collaborators,
          owner: currentFolderOwner,
        } = currentFolder;

        dispatch(updateSelectedWorkspace(workspace));

        if (videos.length === 0) {
          dispatch(updateAllVideosFetched(true));
        }

        dispatch(
          updateCurrentFolderInfo({
            id: currentFolderId,
            name: currentFolderName,
            description: currentFolderDescription,
            parentId: currentFolderParentId,
            favorite,
            shared,
            collaborators,
            owner: currentFolderOwner,
          })
        );
        dispatch(updateVideos(videos));
        dispatch(updateFolders(folders));

        return foldersAndVideosJSON;
      })
      .then(({ current_folder: currentFolder }) => {
        dispatch(fetchAllParentsForBreadcrumbCreator(currentFolder.id));
      })
      .catch(err => {
        if (err) {
          dispatch(
            _showAndLogError(
              err,
              'Oops! Error while fetching videos and folders by name'
            )
          );
          dispatch(updateFoldersError(err));
          dispatch(updateVideosError(err));
        }
      });
  };
}

export function moveFolderCreator(id, newParentFolderId, destinationWorkspace) {
  return (dispatch, getState) => {
    const {
      is_archived: isArchived,
      folder_id: folderId,
      folder_special_id: currentFolderSpecialId,
      top_level_folders: topLevelFolders,
      breadcrumbs,
      folders,
      top_level_folders,
      favorites,
      workspace,
      root_folder_id,
    } = getState().videos_dashboard;

    const folderToMove = _find(folders, { id });
    const newFoldersArr = _without(folders, folderToMove);
    const allFolders = folders.concat(top_level_folders).concat(favorites);

    const targetFolder = allFolders.find(f => f.id == newParentFolderId);

    dispatch(updateFolders(newFoldersArr));

    if (currentFolderSpecialId === SPECIAL_FOLDER_ID_MY_VIDEOS) {
      dispatch(updateTopLevelFolders(newFoldersArr));
    }

    const body = JSON.stringify({
      parent_folder_id: newParentFolderId,
      workspace: destinationWorkspace,
    });

    return fetch(`/v1/folders/${id}`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'PATCH',
      body,
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .then(() => {
        analytics.track(FOLDER_MOVED, {
          current_parent_folder_id: folderId,
          root_folder_id,
          current_folder_special_id: currentFolderSpecialId,
          from_library: workspace,
          to_library: destinationWorkspace,
          depth: breadcrumbs.length - 1,
          folder_name: folderToMove.name,
          is_archived: isArchived,
          new_parent_folder_id: newParentFolderId,
          in_team_videos: /^\/team-videos/.test(window.location.pathname),
          receiving_folder_type:
            targetFolder && targetFolder.shared ? 'public' : 'private',
          id,
          organization_id: folderToMove.organization_id,
        });

        if (newParentFolderId === SPECIAL_FOLDER_ID_MY_VIDEOS) {
          dispatch(
            updateTopLevelFolders(topLevelFolders.concat([folderToMove]))
          );
        }

        dispatch(
          showConfirmationModal(
            `${truncateText(folderToMove.name)} successfully moved`
          )
        );
      })
      .catch(err => {
        if (err) {
          dispatch(_showAndLogError(err, 'Oops! Error while moving folder'));
          dispatch(updateFolders(folders));

          if (currentFolderSpecialId === SPECIAL_FOLDER_ID_MY_VIDEOS) {
            dispatch(updateTopLevelFolders(folders));
          }
        }
      });
  };
}

export function moveVideoCreator(
  id,
  newFolderId,
  archived,
  destinationWorkspace
) {
  return (dispatch, getState) => {
    const { videos_dashboard, workspace: workspaceReducer, user } = getState();
    const {
      is_archived: isArchived,
      folder_id: currentFolderId,
      folder_special_id: folderSpecialId,
      total_video_count: totalVideoCount,
      breadcrumbs,
      videos,
      folders,
      top_level_folders,
      favorites,
      workspace,
    } = videos_dashboard;

    const { capabilities } = user;

    const isMyVideos = folderSpecialId === SPECIAL_FOLDER_ID_MY_VIDEOS;
    const hasLoomPro = capabilities.includes(LOOM_PRO_CAPABILITY);
    const basicUserUX = isMyVideos && !hasLoomPro && !archived;

    const videoToMove = _find(videos, { id });

    const newVideosArr = _without(videos, videoToMove);
    const allFolders = folders.concat(top_level_folders).concat(favorites);
    const targetFolder = allFolders.find(f => f.id == newFolderId);

    if (!basicUserUX) {
      dispatch(updateVideosGrid(newVideosArr));
    }

    if (
      workspace === TEAM_WORKSPACE &&
      newFolderId === SPECIAL_FOLDER_ID_ARCHIVED
    ) {
      newFolderId = SPECIAL_FOLDER_ID_TEAM_ARCHIVED;
    }

    let body;

    if (archived != null) {
      body = JSON.stringify({
        folder_id: newFolderId,
        archived,
      });

      if (archived) {
        dispatch(updateTotalVideoCount(Math.max(0, totalVideoCount - 1)));
      }
    } else {
      body = JSON.stringify({ folder_id: newFolderId });
    }

    return fetch(`/v1/videos/${id}`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'PATCH',
      body,
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .then(() => {
        analytics.track(VIDEO_MOVED, {
          archiving: archived,
          current_folder_id: currentFolderId,
          depth: breadcrumbs.length - 1,
          is_archived: isArchived,
          new_folder_id: newFolderId,
          video_name: videoToMove.name,
          recording_version: videoToMove.video_properties?.recording_version,
          receiving_folder_type:
            targetFolder && targetFolder.shared ? 'public' : 'private',
          id,
          from_library: workspace,
          to_library: destinationWorkspace,
          organization_id: videoToMove.organization_id,
          source: 'video-dashboard-dnd',
        });

        applyOnboardingStateChanges(
          dispatch,
          workspaceReducer.workspaces,
          destinationWorkspace
        );

        dispatch(
          showConfirmationModal(
            `${truncateText(videoToMove.name)} successfully moved`
          )
        );
        dispatch(fetchLibraryVideoCountsCreator());
      })
      .catch(err => {
        if (err) {
          dispatch(_showAndLogError(err, 'Oops! Error moving video'));
          dispatch(updateVideosGrid(videos));
        }
      });
  };
}

export function restoreFromPastCreator() {
  return (dispatch, getState) => {
    const { past } = getState().videos_dashboard;

    if (past.length === 0) {
      window.location.href = ROOT_PAGE;

      return;
    }

    dispatch(restoreFromPast());
  };
}

export function toggleDropdownCreator(id) {
  return (dispatch, getState) => {
    const { card_dropdown_id } = getState().videos_dashboard;

    dispatch(closeDropdowns());

    if (card_dropdown_id !== id) {
      return dispatch(showDropdown(id));
    }
  };
}

export function deleteFolderCreator(id) {
  return (dispatch, getState) => {
    const { folders } = getState().videos_dashboard;
    const folderToDelete = _find(folders, { id });
    const newFoldersArr = _without(folders, folderToDelete);

    if (
      !window.confirm(
        'Are you sure you would like to permanently delete ' +
          `${folderToDelete.name} and all of its contents?`
      )
    ) {
      return;
    }

    dispatch(updateFolders(newFoldersArr));

    return fetch(`/v1/folders/${id}`, {
      credentials: 'include',
      method: 'DELETE',
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .catch(err => {
        if (err) {
          dispatch(_showAndLogError(err, 'Oops! Error deleting folder'));
          dispatch(updateFolders(folders));
        }
      });
  };
}

const applyOnboardingStateChanges = (
  dispatch,
  workspaces,
  destinationWorkspace
) => {
  const selectedWorkspace = workspaces?.find(workspace => workspace.isSelected);

  if (!selectedWorkspace) {
    return;
  }

  if (
    destinationWorkspace === TEAM_WORKSPACE &&
    cardHasBeenCompleted(selectedWorkspace.onboarding, SHARE_VIDEO_CARD_KEY)
  ) {
    markEligibleElementAsCompleteForSelectedWorkspace(
      dispatch,
      workspaces,
      ADD_VIDEO_TO_TEAM_CARD_KEY
    );
  }
};

const removeSingle = (id, items) => items.filter(item => item.id !== id);
const removeAfter = (id, items) => {
  const itemIndex = items.findIndex(item => item.id === id);

  if (itemIndex < 0) {
    return;
  }

  return items.slice(0, itemIndex);
};

const addMultiple = (id, items, allItems, type) => {
  const lastCheckedItem = [...items].pop();
  const lastItem = allItems.findIndex(item => item.id === lastCheckedItem.id);
  const currentItem = allItems.findIndex(item => item.id === id);

  const sliced =
    currentItem > lastItem
      ? allItems.slice(lastItem > 0 ? lastItem : 0, currentItem + 1)
      : allItems.slice(currentItem, lastItem + 1);

  const newItems = sliced
    .map(item => ({
      type,
      name: item.name,
      id: item.id,
    }))
    .filter(item => items.find(itm => itm.id !== item.id));

  const checkedItems = [];
  const map = new Map();

  // make sure items are distinct
  for (const item of [...items, ...newItems]) {
    if (!map.has(item.id)) {
      map.set(item.id, true);
      checkedItems.push(item);
    }
  }

  return checkedItems;
};

export function updateCheckedItemsCreator(
  id,
  checked,
  itemType,
  name,
  visibility
) {
  return (dispatch, getState) => {
    try {
      const { checked_items: checkedItems } = getState().videos_dashboard;
      const itemToActOn = _find(checkedItems, { id });

      if (!checked && !itemToActOn) {
        throw new Error('Error updating checked items');
      }

      let newCheckedItemsArray;

      if (checked) {
        newCheckedItemsArray = _without(checkedItems, itemToActOn).concat([
          {
            type: itemType,
            name,
            id,
            visibility,
          },
        ]);
      } else {
        newCheckedItemsArray = _without(checkedItems, itemToActOn);
      }

      dispatch(updateCheckedItems(newCheckedItemsArray));
    } catch (err) {
      dispatch(
        _showAndLogError(err, 'Oops! Error in update checked items creator')
      );
    }
  };
}

export function itemsCheckedCreator(id, checked, type, name, mode, ownerId) {
  return (dispatch, getState) => {
    try {
      const {
        checked_items: checkedItems,
        videos,
        folders,
      } = getState().videos_dashboard;

      let newItems = [];

      if (mode !== 'shift') {
        newItems = checked
          ? [...checkedItems, { id, name, type, ownerId }]
          : removeSingle(id, checkedItems);
      }

      if (mode === 'shift') {
        newItems = checked
          ? addMultiple(
              id,
              checkedItems,
              type === VIDEO ? videos : folders,
              type
            )
          : removeAfter(id, checkedItems);
      }

      dispatch(updateCheckedItems(newItems));

      if (checked) {
        requestAnimationFrame(() => {
          const video = document.querySelector(`#video_card_${id}`);

          video &&
            video.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'center',
            });
        });
      }
    } catch (err) {
      dispatch(
        _showAndLogError(err, 'Oops! Error in update checked items creator')
      );
    }
  };
}

export function bulkDeleteFoldersCreator(folderIds) {
  return async (dispatch, getState) => {
    try {
      const { folders } = getState().videos_dashboard;

      const client = getGraphQLClient();

      const { data } = await client.mutate({
        mutation: BulkDeleteFolders,
        variables: { folderIds },
      });

      if (data.bulkDeleteFolders?.success) {
        const remainingFolders = folders.filter(folder => {
          if (!folderIds.includes(folder.id)) {
            return folder;
          }
        });

        dispatch(updateFolders(remainingFolders));
      } else {
        dispatch(showErrorBar(ERROR, 'Error deleting folders'));
      }
    } catch (err) {
      sentry.logException(err, 'Error bulk deleting folders');
      dispatch(showErrorBar(ERROR, 'Error deleting folders'));
    }
  };
}

// update router history if item is favorited/unfavorited from sidebar
// and is not in current folder
export function updatePastForFavoriteCreator(id, favorite) {
  return (dispatch, getState) => {
    const { folders, past } = getState().videos_dashboard;
    const folderInCurrentGrid = _find(folders, { id });

    if (folderInCurrentGrid) {
      return;
    }

    const pastFolderToAdjustIndex = _findIndex(past, pastItem => {
      const pastItemHasFolder = _some(pastItem.folders, { id });

      if (pastItemHasFolder) {
        return true;
      }

      return false;
    });

    if (pastFolderToAdjustIndex === -1) {
      return;
    }

    const pastFolderToAdjust = Object.assign({}, past[pastFolderToAdjustIndex]);
    const pastFolders = pastFolderToAdjust.folders;
    const pastFolderToReplace = _find(pastFolders, { id });
    const newPastFolder = Object.assign({}, pastFolderToReplace, { favorite });
    const newPast = past.slice(0);

    pastFolderToAdjust.folders = pastFolders.map(folder => {
      if (folder.id === newPastFolder.id) {
        return newPastFolder;
      }

      return folder;
    });

    // starting position to delete, number of items, new item
    newPast.splice(pastFolderToAdjustIndex, 1, pastFolderToAdjust);

    return dispatch(updatePast(newPast));
  };
}

// update router history if item has been moved to a folder the user was
// previously on
export function updatePastForMove() {
  return (dispatch, getState) => {
    const {
      checked_items: checkedItems,
      folder_to_move_to: folderToMoveTo,
      folders,
      past,
      videos,
    } = getState().videos_dashboard;

    if (checkedItems.length === 0 || _isEmpty(folderToMoveTo)) {
      return;
    }

    const pastFolderToAdjustIndex = _findIndex(past, pastItem => {
      return (
        pastItem.folder_id === folderToMoveTo.id ||
        pastItem.folder_id === folderToMoveTo.special_id
      );
    });

    if (pastFolderToAdjustIndex === -1) {
      return;
    }

    const pastFolderToAdjust = Object.assign({}, past[pastFolderToAdjustIndex]);

    const checkedFolderItems = checkedItems.filter(
      item => item.type === FOLDER
    );
    const checkedVideoItems = checkedItems.filter(item => item.type === VIDEO);
    const checkedFolders = checkedFolderItems.map(folderItem =>
      _find(folders, { id: folderItem.id })
    );

    const checkedVideos = checkedVideoItems.map(videoItem =>
      _find(videos, { id: videoItem.id })
    );
    const newPastFolders = pastFolderToAdjust.folders.concat(checkedFolders);

    const newPastVideos = pastFolderToAdjust.videos.concat(checkedVideos);
    const sortingDateFormat = 'YYYYMMDD';

    pastFolderToAdjust.folders = _sortBy(newPastFolders, folder => {
      return new moment(folder.createdAt).format(sortingDateFormat);
    }).reverse();

    pastFolderToAdjust.videos = _sortBy(newPastVideos, video => {
      return new moment(video.createdAt).format(sortingDateFormat);
    }).reverse();

    const newPast = past.slice(0);

    // starting position to delete, number of items, new item
    newPast.splice(pastFolderToAdjustIndex, 1, pastFolderToAdjust);

    return dispatch(updatePast(newPast));
  };
}

export function deleteVideoCreator(id) {
  return async (dispatch, getState) => {
    try {
      const { videos } = getState().videos_dashboard;
      const client = getGraphQLClient();

      const { data } = await client.mutate({
        mutation: DestroyVideo,
        variables: {
          videoId: id,
          video_properties: { ui_location: DASHBOARD },
        },
      });

      if (data?.destroyVideo?.success) {
        const videoToDelete = _find(videos, { id });
        const newVideosArr = _without(videos, videoToDelete);

        dispatch(updateVideosGrid(newVideosArr));
      } else {
        const msg = data?.destroyVideo?.message;

        if (msg) {
          throw new Error(msg);
        }
      }
    } catch (err) {
      sentry.logException(err, `Error deleting video ${id}`);
      dispatch(showErrorBar(ERROR, 'Error deleting video'));
    }
  };
}

export function bulkDeleteVideosCreator(videoIds) {
  return async (dispatch, getState) => {
    try {
      const { videos } = getState().videos_dashboard;

      const client = getGraphQLClient();

      const { data } = await client.mutate({
        mutation: BulkDeleteVideos,
        variables: { videoIds },
      });

      if (data.bulkDeleteVideos?.success) {
        const remainingVideos = videos.filter(video => {
          if (!videoIds.includes(video.id)) {
            return video;
          }
        });

        dispatch(updateVideosGrid(remainingVideos));
      } else {
        dispatch(showErrorBar(ERROR, 'Error deleting videos'));
      }
    } catch (err) {
      sentry.logException(err, 'Error bulk deleting videos');
      dispatch(showErrorBar(ERROR, 'Error deleting videos'));
    }
  };
}

export function loadPublicFolderCreator(folderId, subPath = []) {
  return (dispatch, getState) => {
    const {
      is_fetching_folders: isFetchingFolders,
      is_fetching_videos: isFetchingVideos,
    } = getState().videos_dashboard;

    // We don't want to handle multiple streams of incoming folder info, so stick to 1 at a time
    if (isFetchingFolders || isFetchingVideos) {
      return;
    }

    dispatch(fetchFolders());

    if (subPath.length > 0) {
      dispatch(fetchVideos());

      const body = JSON.stringify({
        folder_names: subPath,
        parent_folder_id: folderId,
      });

      // - by_name returns both videos and folders
      //   and is used on initial page load
      return fetch('/v1/folders/by_name', {
        credentials: 'include',
        headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
        method: 'POST',
        body,
      })
        .then(response => _throwOnHttpError(response, dispatch))
        .then(resp => resp.json())
        .then(foldersAndVideosJSON => {
          const {
            current_folder: currentFolder,
            folders,
            videos,
          } = foldersAndVideosJSON;

          if (videos.length === 0) {
            dispatch(updateAllVideosFetched(true));
          }

          dispatch(
            updateCurrentFolderInfo({
              id: currentFolder.id,
              name: currentFolder.name,
              description: currentFolder.description,
              parentId: currentFolder.parent_folder_id,
              ownerId: currentFolder.owner_id,
              favorite: currentFolder.favorite,
              shared: currentFolder.shared,
            })
          );
          dispatch(updateVideos(videos));
          dispatch(updateFolders(folders));

          return foldersAndVideosJSON;
        })
        .then(({ folder }) => {
          dispatch(fetchAllParentsForBreadcrumbCreator(folder.id));
        })
        .catch(err => {
          if (err) {
            dispatch(
              _showAndLogError(
                err,
                'Oops! Error while fetching shared videos and folders by name'
              )
            );
            dispatch(updateFoldersError(err));
            dispatch(updateVideosError(err));
          }
        });
    }

    // This endpoint is used when clicking into a new folder and page doesn't reload
    const endpoint =
      `/v1/folders/${folderId}?content_type=folder` +
      `&limit=${FOLDERS_QUERY_LIMIT}`;

    return fetch(endpoint, {
      credentials: 'include',
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .then(resp => resp.json())
      .then(foldersJSON => {
        const { folders, folder } = foldersJSON;

        dispatch(
          updateCurrentFolderInfo({
            id: folder.id,
            name: folder.name,
            description: folder.description,
            parentId: folder.parent_folder_id,
            ownerId: folder.owner_id,
            favorite: folder.favorite,
            shared: folder.shared,
          })
        );
        dispatch(updateFolders(folders));

        return foldersJSON;
      })
      .then(({ folder }) => {
        dispatch(resetVideos());
        dispatch(fetchVideosCreator(folderId));
        dispatch(fetchAllParentsForBreadcrumbCreator(folder.id));
      })
      .catch(err => {
        if (err) {
          dispatch(
            _showAndLogError(err, 'Oops! Error in fetch folders creator')
          );
          dispatch(updateFoldersError(err));
        }
      });
  };
}

export function goToFolderCreator(
  { id, name, description, favorite, shared, collaborators },
  subPath
) {
  // In cases where it's possible the folder name may not be unique and cannot be used
  // to correctly determine the folder on the backend (such as when viewing favorites
  // or shared-with-me) we have to append the folder ID to the folder name in the URL.
  // We don't want to display the folder ID in the breadcrumb, so we'll remove it here
  // if it's present so we can have the proper folder name for breadcrumbs.
  const realFolderName = name.replace(URL_FOLDER_ID_REGEX, '');

  return (dispatch, getState) => {
    dispatch(addToPastCreator());

    const isJtbd = getState().videos_dashboard.is_jtbd;
    const rootIsPublic = getState().videos_dashboard.root_is_public;

    const ownerId = getState().videos_dashboard.folder_owner_id;
    const userId = getState().user.current.id;

    const { folders: jtbdFolders, videos: jtbdTopLevelVideos } =
      getState().user_jtbd;

    if (id == FOLDER_NAME_JTBD || isJtbd) {
      // Entering a JTBD folder

      dispatch(appendAndGotoUrlCreator(name));
      dispatch(resetVideos());

      analytics.track(CLICKED_JTBD_FOLDER, { name });

      if (name === FOLDER_NAME_HOW_TO_USE_LOOM) {
        const otherFolder = jtbdFolders[0].folder;

        otherFolder.name = FOLDER_NAME_OTHER;

        dispatch(updateIsJTBD(true));
        dispatch(updateFolders([otherFolder]));
        dispatch(updateVideos(jtbdTopLevelVideos));
      } else if (isJtbd && name === FOLDER_NAME_OTHER) {
        // 'Other' subfolder
        const allChildFolders = jtbdFolders[0].children.map(
          child => child.folder
        );

        dispatch(updateFolders(allChildFolders));
        dispatch(updateVideos([]));
      } else {
        // Regular JTBD subfolder
        const allChildFolders = jtbdFolders[0].children.map(
          child => child.folder
        );
        const newCurrentFolder = _find(
          allChildFolders,
          childFolder => childFolder.name === name
        );

        dispatch(updateFolders([]));
        dispatch(updateVideos(newCurrentFolder.Videos));
      }

      const { breadcrumbs } = getState().videos_dashboard;

      dispatch(updateBreadcrumbs(breadcrumbs.concat({ id, name })));

      return;
    } else if (rootIsPublic) {
      // Entering subfolder to public folder
      analytics.track(SHARE_FOLDER_FOLDER_CLICKED, {
        owner: userId == ownerId,
      });

      const targetFolder = getState().videos_dashboard.folders.find(
        folder => folder.id == id
      );

      dispatch(appendAndGotoUrlCreator(targetFolder.name));
      dispatch(loadPublicFolderCreator(id, subPath));
    } else {
      // Entering a normal folder
      const { breadcrumbs } = getState().videos_dashboard;

      dispatch(appendAndGotoUrlCreator(name));
      dispatch(
        fetchFoldersCreator(id, name, description, favorite, collaborators)
      );
      dispatch(resetVideos());
      dispatch(fetchVideosCreator(id));
      dispatch(
        updateBreadcrumbs(
          breadcrumbs.concat({ id, name: realFolderName, shared })
        )
      );
    }
  };
}

export function shareFolder(id, name, fastLoading = false, analyticsLocation) {
  return (dispatch, getState) => {
    if (fastLoading) {
      dispatch(shareFolderLoading(id, name));
      dispatch(shareFolderSuccess());

      return Promise.resolve();
    }

    dispatch(shareFolderLoading(id, name));

    // - Delay loading 0-2s to ensure user understands that
    //   folder wasn't already shared and something is happening
    // - This was a UX decision, so users won't think all folders are always shared

    const fetchPromise = fetch(`/v1/folders/${id}`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'PATCH',
      body: JSON.stringify({
        shared: true,
        analytics_location: analyticsLocation,
      }),
    }).then(response => _throwOnHttpError(response, dispatch));

    const timeoutPromise = new Promise(accept => setTimeout(accept, 2000));

    return Promise.all([fetchPromise, timeoutPromise])
      .then(() => {
        dispatch(shareFolderSuccess(id));

        if (getState().preview_panel.id === id) {
          dispatch(updatePreviewShareStatus(true));
        }
      })
      .catch(err => {
        if (err) {
          dispatch(_showAndLogError(err, 'Oops! Error while sharing folder'));
          dispatch(shareFolderFailed(err));
        }
      });
  };
}

export function unshareFolder(id) {
  return (dispatch, getState) => {
    dispatch(unshareFolderLoading(id));

    return fetch(`/v1/folders/${id}`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'PATCH',
      body: JSON.stringify({ shared: false }),
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .then(resp => resp.json())
      .then(folder => {
        dispatch(unshareFolderSuccess(id));
        dispatch(
          showConfirmationModal(
            `${truncateText(folder.name)} is now a private folder`
          )
        );

        if (getState().preview_panel.id === id) {
          dispatch(updatePreviewShareStatus(false));
        }
      })
      .catch(err => {
        if (err) {
          dispatch(
            _showAndLogError(err, 'Oops! Error while making folder private')
          );
          dispatch(unshareFolderFailed(err));
        }
      });
  };
}

const patchFolderDescription = _debounce(
  (dispatch, folderId, description) => {
    fetch(`/v1/folders/${folderId}`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'PATCH',
      body: JSON.stringify({ description }),
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .catch(err => {
        if (err) {
          dispatch(
            _showAndLogError(
              err,
              'Oops! Error while changing folder description'
            )
          );
        }
      });
  },
  500,
  { maxWait: 2000 }
);

export function folderDescriptionChangedCreator(description) {
  return (dispatch, getState) => {
    const { folder_id: folderId } = getState().videos_dashboard;

    dispatch(folderDescriptionChanged(description));

    patchFolderDescription(dispatch, folderId, description);
  };
}

export function updateTotalVideoCountCreator() {
  return dispatch => {
    fetch('/v1/videos/active_count', {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
    })
      .then(response => _throwOnHttpError(response, dispatch))
      .then(resp => resp.json())
      .then(({ count }) => dispatch(updateTotalVideoCount(count)))
      .catch(err => {
        if (err) {
          dispatch(
            _showAndLogError(
              err,
              'Oops! Error while updating total videos count'
            )
          );
        }
      });
  };
}

export function getUserFavoritesCreator() {
  return async (dispatch, getState) => {
    try {
      const userId = getState().user.current.id;
      const client = getGraphQLClient();
      const folders = [];
      const videos = [];
      const { data } = await client.query({
        query: GetUserFavorites,
        variables: { userId },
        fetchPolicy: 'no-cache',
      });

      data.userFavorites.forEach(favorite => {
        if (favorite.entity_type === 'video') {
          videos.push(favorite.video);
        } else {
          folders.push(favorite.folder);
        }
      });

      // TEMP(claudio): remove favorite when archived
      dispatch(updateVideos(videos.filter(video => video.archived !== true)));
      dispatch(updateFolders(folders));
      dispatch(updateAllVideosFetched(true));
      dispatch(updateCurrentFolderInfo(SPECIAL_FOLDER_FAVORITES));
    } catch (err) {
      sentry.logException(err, 'Error fetching user favorites');
      dispatch(showErrorBar(ERROR, 'Error fetching user favorites'));
    }
  };
}

export function getSharedItemsCreator() {
  return async dispatch => {
    try {
      const client = getGraphQLClient();
      let folders = [];
      let videos = [];

      const [topLevelEntities, sharedFolders, sharedVideos] = await Promise.all(
        [
          client.query({
            query: getTopLevelSharedEntities,
            fetchPolicy: 'no-cache',
          }),
          client.query({
            query: GetSharedFoldersOwned,
            fetchPolicy: 'no-cache',
          }),
          client.query({
            query: GetSharedVideosOwned,

            fetchPolicy: 'no-cache',
          }),
        ]
      );

      topLevelEntities.data.sharedWorkspaceTopLevelEntitiesForUser.forEach(
        share => {
          if (share.entity_type === 'video') {
            videos.push(share.video);
          } else {
            folders.push(share.folder);
          }
        }
      );

      folders = [...folders, ...sharedFolders.data.sharedFoldersOwned];
      videos = [...videos, ...sharedVideos.data.sharedVideosOwned];

      dispatch(updateSelectedWorkspace(SHARED_WORKSPACE));
      dispatch(updateVideos(videos));
      dispatch(updateFolders(folders));
      dispatch(updateAllVideosFetched(true));
      dispatch(updateCurrentFolderInfo(SPECIAL_FOLDER_SHARED_WITH_ME));
    } catch (err) {
      dispatch(_showAndLogError(err, 'Error fetching shared items'));
    }
  };
}

export function bulkAddFavoritesCreator() {
  return async (dispatch, getState) => {
    const {
      checked_items: checkedItems,
      folder_id: currentFolderId,
      folder_special_id: currentFolderSpecialId,
      breadcrumbs,
      workspace,
    } = getState().videos_dashboard;
    const client = getGraphQLClient();

    const entities = checkedItems.map(item => {
      return {
        id: item.id,
        type: item.type,
      };
    });

    const { data } = await client.mutate({
      mutation: AddFavorite,
      variables: { entities },
    });

    if (data.addFavorite) {
      dispatch(showConfirmationModal('Items successfully starred'));

      entities.forEach(entity => {
        if (entity.type === FOLDER) {
          dispatch(updateUserFavoriteFolder(entity.id, true));

          analytics.track(FOLDER_FAVORITED, {
            current: false,
            current_parent_folder_id: currentFolderId,
            current_folder_special_id: currentFolderSpecialId,
            depth: breadcrumbs.length - 1,
            favorite: true,
            workspace,
            id: entity.id,
          });
        } else {
          dispatch(updateUserFavoriteVideo(entity.id, true));

          analytics.track(VIDEO_FAVORITED, {
            favorite: true,
            workspace,
            id: entity.id,
          });
        }
      });
    }
  };
}

export function bulkRemoveFavoritesCreator() {
  return async (dispatch, getState) => {
    const {
      checked_items: checkedItems,
      folder_id: currentFolderId,
      folder_special_id: currentFolderSpecialId,
      breadcrumbs,
      workspace,
    } = getState().videos_dashboard;
    const client = getGraphQLClient();

    const entities = checkedItems.map(item => {
      return {
        id: item.id,
        type: item.type,
      };
    });

    const { data } = await client.mutate({
      mutation: DeleteFavorite,
      variables: { entities },
    });

    if (!data.addFavorite) {
      dispatch(showConfirmationModal('Items successfully unstarred'));

      entities.forEach(entity => {
        if (entity.type === FOLDER) {
          dispatch(updateUserFavoriteFolder(entity.id, false));

          analytics.track(FOLDER_FAVORITED, {
            current: false,
            current_parent_folder_id: currentFolderId,
            current_folder_special_id: currentFolderSpecialId,
            depth: breadcrumbs.length - 1,
            favorite: false,
            workspace,
            id: entity.id,
          });
        } else {
          dispatch(updateUserFavoriteVideo(entity.id, false));

          analytics.track(VIDEO_FAVORITED, {
            favorite: false,
            workspace,
            id: entity.id,
          });
        }
      });
    }
  };
}

export function toggleFolderFavoriteCreator(id, forceFavorite = false) {
  let isFavorite;

  return async (dispatch, getState) => {
    try {
      const client = getGraphQLClient();
      const {
        folders,
        folder_id: currentFolderId,
        folder_special_id: current_folder_special_id,
        breadcrumbs,
        workspace,
      } = getState().videos_dashboard;
      let item = null;

      for (const folder of folders) {
        if (folder.id !== id) {
          continue;
        }

        isFavorite = forceFavorite ? true : !folder.isUserFavorite;
        item = { id: folder.id, type: 'folder' };
        break;
      }

      if (!item) {
        return;
      }

      // optimistic update
      dispatch(updateUserFavoriteFolder(id, forceFavorite ? true : isFavorite));

      const { data } = await client.mutate({
        mutation: isFavorite ? AddFavorite : DeleteFavorite,
        variables: { entities: [item] },
      });

      if (!data.addFavorite && !data.deleteFavorite) {
        dispatch(updateUserFavoriteFolder(id, !isFavorite));
      }

      dispatch(
        showConfirmationModal(
          `Folder successfully ${isFavorite ? '' : 'un'}starred`
        )
      );

      analytics.track(FOLDER_FAVORITED, {
        current: false,
        current_parent_folder_id: currentFolderId,
        current_folder_special_id,
        depth: breadcrumbs.length - 1,
        favorite: isFavorite,
        workspace,
        id,
      });
    } catch (err) {
      sentry.logException(err, 'Error toggle folder favorite');
      dispatch(updateUserFavoriteFolder(id, !isFavorite));
      dispatch(showErrorBar(err, 'Error adding favorite'));
    }
  };
}

export function toggleVideoFavoriteCreator(id, forceFavorite = false, video) {
  let isFavorite;

  return async (dispatch, getState) => {
    try {
      const client = getGraphQLClient();
      let favoriteVideo = video;
      let workspace = SHARE_PAGE;

      if (!favoriteVideo) {
        const videosDashboard = getState().videos_dashboard;
        const videos = videosDashboard.videos;

        workspace = videosDashboard.workspace;

        favoriteVideo = videos.find(video => video.id === id);
      }

      if (!favoriteVideo) {
        return;
      }

      isFavorite = forceFavorite ? true : !favoriteVideo.isUserFavorite;

      // optimistic update
      dispatch(updateUserFavoriteVideo(id, isFavorite));

      const { data } = await client.mutate({
        mutation: isFavorite ? AddFavorite : DeleteFavorite,
        variables: { entities: [{ id, type: 'video' }] },
      });

      if (!data.addFavorite && !data.deleteFavorite) {
        dispatch(updateUserFavoriteVideo(id, !isFavorite));
      }

      analytics.track(VIDEO_FAVORITED, {
        favorite: isFavorite,
        workspace,
        id,
      });
    } catch (err) {
      sentry.logException(err, 'Error toggle folder favorite');
      dispatch(updateUserFavoriteVideo(id, !isFavorite));
      dispatch(showErrorBar(err, 'Error adding favorite'));
    }
  };
}

export function toggleSharedFolderCreator(id, isShared) {
  return async dispatch => {
    try {
      const client = getGraphQLClient();

      const { data } = await client.mutate({
        mutation: ToggleSharedFolder,
        variables: { id, isShared },
      });

      if (data.toggleSharedFolder) {
        if (isShared) {
          dispatch(shareFolderSuccess(id));
        } else {
          dispatch(unshareFolderSuccess(id));
        }
      }
    } catch (err) {
      sentry.logException(err, 'Error toggle shared folder');
      dispatch(showErrorBar(err, 'Error managing shared folder'));
    }
  };
}
